<script setup lang="ts">
import {useDialogPluginComponent} from 'quasar';
import {useI18n} from 'vue-i18n';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';

defineEmits([
  ...useDialogPluginComponent.emits,
]);

// Copy is overridable so the dialog can speak to the user's actual renewal path
// (a voucher user renews with a code, a direct-payment/gifted user picks a plan).
// Defaults keep the generic "choose a plan" wording for the no-contract case.
// The override keys are typed at the call site (see ContentLockedDialogCopy in
// account-subscription-tab-page.vue) so a mistyped key fails to compile there.
const props = withDefaults(defineProps<{
  subtitleKey?: string;
  descriptionKey?: string;
  ctaKey?: string;
}>(), {
  subtitleKey: 'content_locked_subtitle',
  descriptionKey: 'content_locked_description',
  ctaKey: 'choose_a_plan',
});

const {t} = useI18n();
const {dialogRef, onDialogOK} = useDialogPluginComponent();
</script>

<template>
  <q-dialog
    ref="dialogRef"
    persistent
    :aria-label="t('content_locked_title')"
    transition-show="jump-up"
    transition-hide="jump-down"
  >
    <div class="content-locked-dialog">
      <div class="x-close-wrapper">
        <flat-icon-btn
          class="x-close"
          icon="x-close.svg"
          :label="t('aria_close_modal')"
          @click="onDialogOK"
        />
      </div>
      <div class="dialog-state">
        <div class="dialog-icon">
          <svg-illustration name="payment-sun.svg"/>
        </div>
        <h2 class="dialog-title">
          {{ t('content_locked_title') }}
        </h2>
        <p class="dialog-subtitle">
          {{ t(props.subtitleKey) }}
        </p>
        <p class="dialog-description">
          {{ t(props.descriptionKey) }}
        </p>
        <div class="dialog-actions">
          <balm-btn
            :label="t(props.ctaKey)"
            icon="arrow-narrow-right.svg"
            theme="grey"
            paddings="p3-7"
            class="full-width"
            @click="onDialogOK"
          />
        </div>
      </div>
    </div>
  </q-dialog>
</template>

<style scoped lang="scss">
.content-locked-dialog {
  position: relative;
  overflow: hidden;
  padding: var(--Spacing-spacing-4);
  padding-top: var(--Spacing-spacing-4);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--Neutral-UI-neutral-ui-10);
  border-radius: var(--card-border-radius);

  @include screen-md {
    min-width: 460px;
  }
}

.dialog-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-height: 240px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.4s ease-out;
}

.dialog-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--Spacing-spacing-4);
}

.dialog-title {
  @include font-display-3;
  margin-bottom: var(--Spacing-spacing-5);
  color: var(--Neutral-UI-neutral-ui-100);
}

.dialog-subtitle {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-3);
}

.dialog-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin-bottom: var(--Spacing-spacing-5);
  max-width: 360px;
  line-height: 1.6;
}

.dialog-actions {
  display: flex;
  gap: var(--Spacing-spacing-3);
  flex-direction: column;
  width: 100%;
}

.full-width {
  width: 100%;
}

.x-close-wrapper {
  position: absolute;
  right: var(--Spacing-spacing-6);
  top: var(--Spacing-spacing-6);
  background-color: inherit;
  border-radius: 50%;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
